import clsx from 'clsx'
import { useEffect } from 'react'
import { useRouter } from 'next/router'
import useTranslation from 'next-translate/useTranslation'
import CloseIcon from '@ancon/wildcat-ui/shared/icons/close.svg'
import GlobeIcon from '@ancon/wildcat-ui/shared/icons/globe-outline.svg'

import Button from '../../../../app/components/Button'
import HeadingText from '../../../../app/components/HeadingText'
import BodyText from '../../../../app/components/BodyText'
import useAppSelector from '../../../../../store/hooks/useAppSelector'
import { appIsMobileDrawerVisibleSelector } from '../../../../app/store/appSelectors'
import useAppDispatch from '../../../../../store/hooks/useAppDispatch'
import {
  appSetIsMobileDrawerVisible,
  appSetIsSwitchLanguageModalVisible,
} from '../../../../app/store/appSlice'
import {
  clientContextCurrentUserCompanyNameSelector,
  clientContextCustomerNameSelector,
  clientContextHasCompanyProfilesSelector,
  clientContextIsAnonymousCustomerSelector,
  clientContextIsCompanyUserSelector,
  clientContextProfileNameSelector,
} from '../../../../clientContext/store/clientContextSelectors'
import {
  authCustomerIsLoggedInSelector,
  authSignOutPendingSelector,
  authPendingSignOutCheckoutUpdateSelector,
} from '../../../../auth/store/authSelectors'
import isAppWhiteLabeled from '../../../../app/utils/isAppWhiteLabeled'
import {
  setAuthGuestCustomerOnboardingModalVisible,
  setAuthProfileSelectionModalType,
  showAuthModal,
} from '../../../../auth/store/authSlice'
import InteractiveDiv from '../../InteractiveDiv'
import { AppRoutes } from '../../../../app/constants'
import AuthProfileTypeIcon from '../../../../auth/components/AuthProfileTypeIcon'
import {
  AuthModalType,
  AuthProfileType,
  ProfileSelectionModalType,
} from '../../../../auth/types'
import { preOrderUserSelector } from '../../../../preOrder/store/preOrderSelectors'
import useAppSignOut from '../../../../auth/hooks/useAppSignOut'
import { ProfileAction } from '../../../constants'

import MobileSideDrawerProfileAction from './MobileSideDrawerProfileAction'
import styles from './MobileSideDrawer.module.scss'

export default function MobileSideDrawer() {
  const { t } = useTranslation('common')

  const dispatch = useAppDispatch()

  const router = useRouter()

  const isDrawerVisible = useAppSelector(appIsMobileDrawerVisibleSelector)

  const isSignOutPending = useAppSelector(authSignOutPendingSelector)

  const isPendingSignOutCheckoutUpdate = useAppSelector(
    authPendingSignOutCheckoutUpdateSelector,
  )

  const customerName = useAppSelector(clientContextCustomerNameSelector)

  const isCompanyUser = useAppSelector(clientContextIsCompanyUserSelector)

  const isAnonymousCustomer = useAppSelector(
    clientContextIsAnonymousCustomerSelector,
  )

  const hasCompanyProfiles = useAppSelector(
    clientContextHasCompanyProfilesSelector,
  )

  const isLoggedIn = useAppSelector(authCustomerIsLoggedInSelector)

  const companyName = useAppSelector(
    clientContextCurrentUserCompanyNameSelector,
  )

  const profileName = useAppSelector(clientContextProfileNameSelector)

  const preOrderUser = useAppSelector(preOrderUserSelector)

  const isWhiteLabeled = isAppWhiteLabeled()

  const appSignOut = useAppSignOut()

  function handleCloseDrawer() {
    dispatch(appSetIsMobileDrawerVisible(false))
  }

  function handleSignInClick() {
    handleCloseDrawer()
    dispatch(showAuthModal(AuthModalType.SignIn))
  }

  function handleSignUpClick() {
    handleCloseDrawer()
    dispatch(showAuthModal(AuthModalType.SignUp))
  }

  function handleNavigateToMyOrders() {
    handleCloseDrawer()
    router.push(AppRoutes.Orders)
  }

  function handleNavigateToMyVisits() {
    handleCloseDrawer()
    router.push(AppRoutes.Visits)
  }

  function handleNavigateToMyWallets() {
    handleCloseDrawer()
    const walletsURL = isWhiteLabeled
      ? AppRoutes.WalletsRedirect
      : AppRoutes.Wallets
    router.push(walletsURL)
  }

  function handleNavigateToManageAccount() {
    handleCloseDrawer()
    router.push(AppRoutes.Account)
  }

  function handleCreateProfile() {
    handleCloseDrawer()
    dispatch(setAuthGuestCustomerOnboardingModalVisible(true))
  }

  async function handleSignOut() {
    const isSignOutSuccess = await appSignOut()

    if (isSignOutSuccess) {
      handleCloseDrawer()
    }
  }

  function handleChangeProfile() {
    handleCloseDrawer()
    dispatch(
      setAuthProfileSelectionModalType(
        ProfileSelectionModalType.ProfileSelection,
      ),
    )
  }

  function handleSwitchLanguageClick() {
    dispatch(appSetIsSwitchLanguageModalVisible(true))
  }

  useEffect(() => {
    if (isDrawerVisible) {
      document.body.classList.add(styles.isOpen)
    } else {
      document.body.classList.remove(styles.isOpen)
    }
  }, [isDrawerVisible])

  function getProfileName() {
    if (isLoggedIn) {
      return profileName
    }

    if (preOrderUser) {
      return preOrderUser.name
    }

    return t('auth.hiThere')
  }

  function getProfileAdditionalInfo() {
    if (isCompanyUser) {
      return customerName
    }

    if (isAnonymousCustomer) {
      return t('auth.guest')
    }

    return null
  }

  const profileAdditionalInfo = getProfileAdditionalInfo()

  return (
    <div
      className={clsx(styles.container, {
        [styles.isDrawerVisible]: isDrawerVisible,
      })}
    >
      <div className={styles.headerContainer}>
        <div className={styles.userInfoContainer}>
          <AuthProfileTypeIcon
            type={
              companyName ? AuthProfileType.Company : AuthProfileType.Private
            }
          />
          <div className={styles.userMetaContainer}>
            <HeadingText as="h2">{getProfileName()}</HeadingText>
            {profileAdditionalInfo && (
              <BodyText color="body-1">{profileAdditionalInfo}</BodyText>
            )}
          </div>
        </div>
        <Button variant="secondary" onClick={handleCloseDrawer}>
          <CloseIcon />
        </Button>
      </div>

      <div className={styles.contentContainer}>
        {!isLoggedIn && !preOrderUser && (
          <div className={styles.authActionsContainer}>
            <Button onClick={handleSignInClick}>{t('auth.signIn')}</Button>
            <Button variant="secondary" onClick={handleSignUpClick}>
              {t('auth.signUp')}
            </Button>
          </div>
        )}

        {!!isLoggedIn && (
          <div className={styles.navigationActions}>
            <MobileSideDrawerProfileAction
              action={ProfileAction.MyOrders}
              onClick={handleNavigateToMyOrders}
            />
            {!isAnonymousCustomer && (
              <MobileSideDrawerProfileAction
                action={ProfileAction.MyVisits}
                onClick={handleNavigateToMyVisits}
              />
            )}
            {!isAnonymousCustomer && (
              <MobileSideDrawerProfileAction
                action={ProfileAction.MyWallets}
                onClick={handleNavigateToMyWallets}
              />
            )}
            {!isAnonymousCustomer && (
              <MobileSideDrawerProfileAction
                action={ProfileAction.MyAccount}
                onClick={handleNavigateToManageAccount}
              />
            )}
          </div>
        )}

        {(!!isLoggedIn || !!preOrderUser) && (
          <div className={styles.accountActions}>
            {isAnonymousCustomer && (!preOrderUser || !preOrderUser.isHost) && (
              <MobileSideDrawerProfileAction
                action={ProfileAction.GuestOnBoarding}
                onClick={handleCreateProfile}
              />
            )}
            {hasCompanyProfiles && (
              <MobileSideDrawerProfileAction
                action={ProfileAction.ChangeProfile}
                onClick={handleChangeProfile}
              />
            )}
            <MobileSideDrawerProfileAction
              action={ProfileAction.SignOut}
              onClick={handleSignOut}
              disabled={isSignOutPending || isPendingSignOutCheckoutUpdate}
            />
          </div>
        )}
      </div>

      <div className={styles.footerContainer}>
        <InteractiveDiv
          className={styles.footerItem}
          onClick={handleSwitchLanguageClick}
        >
          <GlobeIcon />
          <BodyText>{t('components.navBar.switchLanguage')}</BodyText>
        </InteractiveDiv>
      </div>
    </div>
  )
}
